import React, { createContext, useContext, useState, useEffect } from 'react';

interface SidebarContextType {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

/**
 * Whether the rail is pinned open is a personal preference, so it outlives the
 * session. Without this it reset to expanded on every reload, which meant anyone
 * who wanted the icon rail re-collapsed it constantly — and, now that collapsing
 * enables hover-to-peek, that nobody could durably opt out of peeking either.
 */
const COLLAPSED_KEY = "joules_sidebar_collapsed";

const readCollapsedPreference = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    // Private mode / blocked storage: fall back to pinned open.
    return false;
  }
};

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(readCollapsedPreference);

  const setSidebarCollapsed = (collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // Preference just won't survive the session; not worth failing the toggle.
    }
  };

  // Handle screen resize for responsive design
  useEffect(() => {
    const checkScreenWidth = () => {
      const mobile = window.innerWidth < 768;
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    // Check on initial load
    checkScreenWidth();

    // Add resize listener
    window.addEventListener("resize", checkScreenWidth);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenWidth);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    // If sidebar is closed and user toggles collapse, open it
    if (!sidebarOpen) {
      setSidebarOpen(true);
    }
  };

  return (
    <SidebarContext.Provider
      value={{
        sidebarOpen,
        sidebarCollapsed,
        setSidebarOpen,
        setSidebarCollapsed,
        toggleSidebar,
        toggleSidebarCollapse,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}